import schoolLogo from '@/assets/school-logo-sm.png';
import { Shimmer } from './ai-elements/Shimmer';

export type SkeletonVariant = 'text' | 'rect' | 'circle';

export interface LoadingSkeletonProps {
  /** Shape of the placeholder block. */
  variant?: SkeletonVariant;
  /** Tailwind size classes, e.g. "h-4 w-32". */
  className?: string;
  /** For variant="text": render this many stacked lines. */
  lines?: number;
  /** Accessible description of what is loading. */
  label?: string;
}

/** Shimmering placeholder block; hidden from assistive tech except its label. */
export function LoadingSkeleton({
  variant = 'text',
  className = '',
  lines,
  label = 'Loading',
}: LoadingSkeletonProps) {
  if (variant === 'text' && lines && lines > 1) {
    return (
      <div role="status" aria-live="polite" className="space-y-2">
        <span className="sr-only">{label}</span>
        {Array.from({ length: lines }, (_, index) => (
          <span
            key={index}
            aria-hidden="true"
            className={`skeleton block ${index === lines - 1 ? 'w-2/3' : 'w-full'} h-4`}
          />
        ))}
      </div>
    );
  }

  const shapeClass =
    variant === 'circle' ? 'skeleton rounded-full h-10 w-10' : variant === 'rect' ? 'skeleton h-24 w-full' : 'skeleton h-4 w-40';

  return (
    <span role="status" aria-live="polite" className="inline-block">
      <span className="sr-only">{label}</span>
      <span aria-hidden="true" className={`${shapeClass} ${className}`} />
    </span>
  );
}

/** Spinner with shimmering status text, used by inline loading states. */
export function PageSpinner({ label = 'Loading page' }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" className="flex min-h-[40vh] flex-col items-center justify-center gap-3">
      <span className="h-8 w-8 animate-spin rounded-full border-4 border-steel-light border-t-steel" aria-hidden="true" />
      <Shimmer as="p" className="text-sm font-semibold tracking-wide">
        {label}
      </Shimmer>
    </div>
  );
}

/**
 * Full-viewport loading screen for first paint and route transitions. The
 * animated credit line sits at the bottom of the page.
 */
export function LoadingScreen({ label = 'Loading Zvishavane High School fees' }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-screen flex-col items-center justify-center gap-6 bg-steel-pale px-6 py-10"
    >
      <img src={schoolLogo} alt="" className="h-20 w-20" />
      <div className="flex flex-col items-center gap-3">
        <span
          className="h-10 w-10 animate-spin rounded-full border-4 border-steel-light border-t-gold"
          aria-hidden="true"
        />
        <Shimmer as="h1" className="text-center text-xl font-bold tracking-tight">
          {label}
        </Shimmer>
      </div>
      <footer className="mt-auto pt-8 text-center">
        <Shimmer as="p" tone="gold" className="text-xs font-bold uppercase tracking-[0.35em]">
          FROM ASRT
        </Shimmer>
      </footer>
    </div>
  );
}

/** Placeholder rows that mimic a data table while loading. */
export function TableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div role="status" aria-busy="true" className="space-y-2 py-2">
      <span className="sr-only">Loading table</span>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {Array.from({ length: cols }, (_, colIndex) => (
            <span key={colIndex} aria-hidden="true" className="skeleton h-4 w-full" />
          ))}
        </div>
      ))}
    </div>
  );
}
